/**
 * Permission Context — fetches user permissions from the Authorization Service
 * and provides permission-checking hooks for the frontend.
 *
 * Usage:
 *   import { usePermissions } from './PermissionContext';
 *
 *   const { hasPermission, hasAnyPermission, isSystemAdmin } = usePermissions();
 *   if (hasPermission('agents:create')) { ... }
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const PermissionContext = createContext(null);

export function PermissionProvider({ children }) {
    const { isAuthenticated, accessToken, apiConfig } = useAuth();
    const [permissions, setPermissions] = useState(new Set());
    const [permissionsLoaded, setPermissionsLoaded] = useState(false);
    const [permissionsFailed, setPermissionsFailed] = useState(false);
    const [loading, setLoading] = useState(false);

    // Fetch permissions from authorization service (the only source of authority)
    const fetchPermissions = useCallback(async () => {
        if (!isAuthenticated || !accessToken) {
            setPermissions(new Set());
            setPermissionsLoaded(false);
            setPermissionsFailed(false);
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(
                `${apiConfig.API_GATEWAY_URL}/authorization/my-permissions`,
                {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                // The effective permission set is the whole answer — a
                // system_admin arrives as the '*' wildcard, so no role concept
                // is needed (or trusted) on the client.
                setPermissions(new Set(data.permissions || []));
                setPermissionsFailed(false);
            } else {
                console.warn('Failed to fetch permissions:', response.status);
                setPermissions(new Set());
                setPermissionsFailed(true);
            }
        } catch (error) {
            console.warn('Permission fetch error:', error.message);
            setPermissions(new Set());
            setPermissionsFailed(true);
        } finally {
            setLoading(false);
            setPermissionsLoaded(true);
        }
    }, [isAuthenticated, accessToken, apiConfig]);

    useEffect(() => {
        fetchPermissions();
    }, [fetchPermissions]);

    // Retry after 30s when fetch failed — seamless recovery when auth service comes back
    useEffect(() => {
        if (!permissionsFailed || !isAuthenticated) return;
        const timerId = setTimeout(fetchPermissions, 30000);
        return () => clearTimeout(timerId);
    }, [permissionsFailed, isAuthenticated, fetchPermissions]);

    // Full access is a property of the server-issued permission set (the '*'
    // wildcard), not of any role the client can see or edit. Exposed for role
    // *pickers* that hide the system_admin option — never for authority.
    const isSystemAdmin = permissions.has('*');

    /**
     * Check if user has a specific permission.
     * Pure lookup against the server-issued set — no role logic.
     */
    const hasPermission = useCallback((permission) => {
        if (permissions.has('*')) return true;
        if (permissions.has(permission)) return true;
        // Check resource wildcard (e.g., "agents:*" covers "agents:read")
        const resource = permission.split(':')[0];
        return permissions.has(`${resource}:*`);
    }, [permissions]);

    /**
     * Check if user has any of the specified permissions.
     */
    const hasAnyPermission = useCallback((...perms) => {
        return perms.some(p => hasPermission(p));
    }, [hasPermission]);

    /**
     * Check if user has all of the specified permissions.
     */
    const hasAllPermissions = useCallback((...perms) => {
        return perms.every(p => hasPermission(p));
    }, [hasPermission]);

    const value = {
        permissions,
        permissionsLoaded,
        permissionsFailed,
        loading,
        isSystemAdmin,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        refreshPermissions: fetchPermissions,
    };

    return (
        <PermissionContext.Provider value={value}>
            {children}
        </PermissionContext.Provider>
    );
}

export function usePermissions() {
    const context = useContext(PermissionContext);
    if (!context) {
        // Return safe defaults when outside PermissionProvider
        // (e.g., public routes that don't need permission checks)
        return {
            permissions: new Set(),
            permissionsLoaded: false,
            permissionsFailed: false,
            loading: false,
            isSystemAdmin: false,
            hasPermission: () => false,
            hasAnyPermission: () => false,
            hasAllPermissions: () => false,
            refreshPermissions: () => {},
        };
    }
    return context;
}

export default PermissionContext;
